import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { kundenAPI } from '../lib/api';

const EMPTY = {
  name: '',
  ust_id: '',
  iban: '',
  default_konto: '',
  default_ust_satz: '',
  debitor_konto: '',
  is_eu_business: false,
};

function cleanPayload(form, isEdit, original) {
  const out = {};
  for (const k of Object.keys(EMPTY)) {
    const v = form[k];
    if (k === 'is_eu_business') {
      if (!isEdit || v !== original[k]) out[k] = v;
      continue;
    }
    const norm = v === '' ? null : v;
    if (!isEdit) {
      if (norm !== null && norm !== false) out[k] = norm;
    } else if (norm !== (original[k] ?? null)) {
      out[k] = norm;
    }
  }
  return out;
}

export default function KundeFormModal({ open, onClose, slug, kunde, onSaved }) {
  const isEdit = !!kunde;
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (kunde) {
      setForm({
        name: kunde.name || '',
        ust_id: kunde.ust_id || '',
        iban: kunde.iban || '',
        default_konto: kunde.default_konto || '',
        default_ust_satz:
          kunde.default_ust_satz !== null && kunde.default_ust_satz !== undefined
            ? String(kunde.default_ust_satz).replace('.00', '')
            : '',
        debitor_konto: kunde.debitor_konto || '',
        is_eu_business: !!kunde.is_eu_business,
      });
    } else {
      setForm(EMPTY);
    }
  }, [kunde, open]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }
    setBusy(true);
    try {
      const payload = cleanPayload(form, isEdit, kunde || {});
      if (isEdit) {
        await kundenAPI.patch(slug, kunde.id, payload);
        toast.success('Aktualisiert');
      } else {
        await kundenAPI.create(slug, payload);
        toast.success('Angelegt');
      }
      if (onSaved) await onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="modal-overlay">
        <Transition.Child as={Fragment}
          enter="fade-enter" enterFrom="fade-enter-from" enterTo="fade-enter-to"
          leave="fade-leave" leaveFrom="fade-leave-from" leaveTo="fade-leave-to">
          <div className="modal-backdrop" aria-hidden="true" />
        </Transition.Child>

        <div className="modal-positioner">
          <Transition.Child as={Fragment}
            enter="slide-enter" enterFrom="slide-enter-from" enterTo="slide-enter-to"
            leave="slide-leave" leaveFrom="slide-leave-from" leaveTo="slide-leave-to">
            <Dialog.Panel className="modal-panel">
              <form onSubmit={onSubmit}>
                <div className="modal-header">
                  <Dialog.Title className="modal-title">
                    {isEdit ? `Kunde bearbeiten: ${kunde.name}` : 'Neuer Kunde'}
                  </Dialog.Title>
                  <button type="button" className="btn-text" onClick={onClose} aria-label="Schließen">
                    <X size={16} />
                  </button>
                </div>

                <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 12 }}>
                  <label style={{ gridColumn: '1 / span 2' }}>
                    Name *
                    <input type="text" value={form.name} onChange={(e) => setField('name', e.target.value)} required autoFocus />
                  </label>

                  <label>
                    USt-IdNr
                    <input type="text" value={form.ust_id} onChange={(e) => setField('ust_id', e.target.value)} placeholder="DE123456789" />
                  </label>

                  <label>
                    IBAN
                    <input type="text" value={form.iban} onChange={(e) => setField('iban', e.target.value)} />
                  </label>

                  <label>
                    Erlös-Konto (default)
                    <input
                      type="text" value={form.default_konto}
                      onChange={(e) => setField('default_konto', e.target.value)}
                      placeholder="z.B. 8400" pattern="\d{3,8}" title="3-8 Ziffern"
                    />
                  </label>

                  <label>
                    USt-Satz Standardwert
                    <select value={form.default_ust_satz} onChange={(e) => setField('default_ust_satz', e.target.value)}>
                      <option value="">— keiner —</option>
                      <option value="0">0%</option>
                      <option value="7">7%</option>
                      <option value="19">19%</option>
                    </select>
                  </label>

                  <label style={{ gridColumn: '1 / span 2' }}>
                    Debitor-Konto (persönliches Sachkonto, optional)
                    <input
                      type="text" value={form.debitor_konto}
                      onChange={(e) => setField('debitor_konto', e.target.value)}
                      placeholder="z.B. 10001 — leer = Sammelkonto Forderungen LuL"
                      pattern="\d{3,8}"
                    />
                  </label>

                  <label style={{ gridColumn: '1 / span 2', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox" checked={form.is_eu_business}
                      onChange={(e) => setField('is_eu_business', e.target.checked)}
                      style={{ width: 'auto' }}
                    />
                    EU-Unternehmen (ig. Lieferung anwendbar)
                  </label>
                </div>

                <div className="actions" style={{ borderTop: 'none', paddingTop: 12 }}>
                  <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                    Abbrechen
                  </button>
                  <button type="submit" className="btn-primary" disabled={busy}>
                    {isEdit ? 'Speichern' : 'Anlegen'}
                  </button>
                </div>
              </form>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
